import type { Timestamp } from "@bufbuild/protobuf";
import { TZDate } from "@date-fns/tz";
import { format } from "date-fns";
import { enUS, ja } from "date-fns/locale";
import { i18n } from "./i18n";

// 有効期限や締切は JST の業務ルールなので、端末のタイムゾーンで描画すると日付がずれる
const BUSINESS_TIME_ZONE = "Asia/Tokyo";

export type CurrentTime = {
  source: "mock" | "server" | "client";
  date: Date;
};

export type DateStyle =
  | "dateTime"
  | "dateTimeSlim"
  | "dateTimeColon"
  | "dateTimeSlash"
  | "dateTimeSeconds"
  | "month"
  | "date"
  | "monthDay"
  | "monthDayWeekday";

// 日付は言語設定に関わらず日本式で統一する（PRD「言語設定に関わらず日本式の表記で統一する」）。
// 店舗や周囲の日本語話者が画面を見て値を突き合わせられることが目的なので、
// 月名や現地の並び順にローカライズしない。ja は既存の漢字表記を維持し、
// それ以外の言語は同じ値を yyyy/MM/dd で描く
const JA_FORMATS = {
  dateTime: "yyyy年MM月dd日 HH時mm分",
  dateTimeSlim: "yyyy年MM月d日HH:mm",
  dateTimeColon: "yyyy年MM月dd日 HH:mm",
  dateTimeSlash: "yyyy/MM/dd HH:mm",
  dateTimeSeconds: "yyyy/MM/dd HH:mm:ss",
  month: "yyyy年MM月",
  date: "yyyy年M月d日",
  monthDay: "M月d日",
  monthDayWeekday: "M月d日（E）",
} as const satisfies Record<DateStyle, string>;

// 曜日だけは数字にできないので、どの言語でも読めるラテン文字の略称（enUS）で出す
const SLASH_FORMATS = {
  dateTime: "yyyy/MM/dd HH:mm",
  dateTimeSlim: "yyyy/MM/dd HH:mm",
  dateTimeColon: "yyyy/MM/dd HH:mm",
  dateTimeSlash: "yyyy/MM/dd HH:mm",
  dateTimeSeconds: "yyyy/MM/dd HH:mm:ss",
  month: "yyyy/MM",
  date: "yyyy/MM/dd",
  monthDay: "MM/dd",
  monthDayWeekday: "MM/dd (EEE)",
} as const satisfies Record<DateStyle, string>;

export function formatDate(
  value: Date | number,
  style: DateStyle = "date",
): string {
  const isJapanese = i18n.locale === "ja";
  return format(
    new TZDate(+value, BUSINESS_TIME_ZONE),
    isJapanese ? JA_FORMATS[style] : SLASH_FORMATS[style],
    { locale: isJapanese ? ja : enUS },
  );
}

// 和暦のように date-fns の書式に載せられない表記を組む場合に、暦日を JST から取り出すために使う
export function toJSTDate(value: Date | number): Date {
  return new TZDate(+value, BUSINESS_TIME_ZONE);
}

export function formatTimestamp(timestamp: Timestamp | undefined) {
  // スキーマ上は undefined のケースが多いため、利便性のため undefined の場合は空文字を返す
  if (!timestamp) {
    return "";
  }
  return formatDate(Number(timestamp.seconds) * 1000, "dateTime");
}

// サーバーの時刻を取得する関数
// 時間による機能の出し分けを行うケースでは、なるべくクライアントの時計を利用したくないために利用する
export async function getCurrentTime(options?: {
  ignoreMock?: boolean;
}): Promise<CurrentTime> {
  const mockServerDate = import.meta.env.VITE_MOCK_SERVER_DATE;
  if (mockServerDate && !options?.ignoreMock) {
    return {
      source: "mock",
      date: new Date(mockServerDate),
    };
  }

  try {
    const d = await fetch(window.location.origin, {
      method: "HEAD",
      cache: "no-cache",
    });
    const serverDateHeader = d.headers.get("Date");

    if (serverDateHeader) {
      return {
        source: "server",
        date: new Date(serverDateHeader),
      };
    }
    return {
      source: "client",
      date: new Date(),
    };
  } catch (e: unknown) {
    if (e instanceof Error) {
      return {
        source: "client",
        date: new Date(),
      };
    }

    throw e;
  }
}

/**
 * 日付文字列からJST（日本時間）のDateオブジェクトを作成する
 * @param dateString - "2025-01-18" または "2025-01-18 23:59:59" 形式の文字列
 */
export function createJSTDate(dateString: string) {
  // スペースで日付と時刻が分かれているかチェック
  const hasTime = dateString.includes(" ");

  if (hasTime) {
    // 時刻が指定されている場合はそのままJSTとして解釈
    return new Date(`${dateString.replace(" ", "T")}+09:00`);
  }

  // 時刻が指定されていない場合は00:00:00として解釈
  return new Date(`${dateString}T00:00:00+09:00`);
}
